import React, { Fragment, useMemo } from "react";
import Icon from "../../Icon";
import { Platform, StyleSheet, View } from "react-native";
import { useTheme } from "../../../hooks/use-theme";
import Root from "./screens/Root";
import GroupSelect from "./screens/GroupSelect";
import CoverSelect from "./screens/CoverSelect";
import WaitingSelect from "./screens/WaitingSelect";
import { useIsScreenSizeMin } from "../../../hooks/useIsScreenSizeMin";
import { createStackNavigator } from "@react-navigation/stack";

export type IJMEditorStackParamList = {
    IJM: undefined;
    GroupSelect: undefined;
    WaitingSelect: undefined;
    CoverSelect: undefined;
};

const Stack = createStackNavigator<IJMEditorStackParamList>();

/**
 * IJMEditorNavigator is a stack navigator for the IJM editor it is used through the use-ijm-editor.ts hook
 * You should never call this component navigation directly, it is used in the IJMEditorNavScreen
 * @constructor
 */
const IJMEditorNavigator: React.FC = () => {
    const theme = useTheme();
    const styles = useStyles();
    const isLarge = useIsScreenSizeMin("large");
    const [StackWrapper, wrapperProps] = useMemo(
        () => (isLarge ? [View, { style: styles.stackWrapper }] : [Fragment, {}]),
        [isLarge, styles.stackWrapper],
    );
    return (
        <StackWrapper {...wrapperProps}>
            <Stack.Navigator
                initialRouteName="IJM"
                screenOptions={{
                    headerMode: "float",
                    headerTransparent: false,
                    detachPreviousScreen: false,
                    headerShadowVisible: false,
                    headerBackButtonDisplayMode: "minimal",
                    headerTitleAlign: "center",
                    headerShown: false,
                    headerStatusBarHeight: 0,
                    cardStyle: { overflow: "visible" },
                    animation: "slide_from_right",
                    headerBackImage: () => (
                        <Icon
                            name="back"
                            size={28}
                            style={{
                                marginLeft: Platform.OS === "ios" ? 10 : 0,
                                marginRight: 5,
                            }}
                        />
                    ),
                    headerBackTestID: "button-back",
                    headerTitleStyle: {
                        ...theme.fonts.titleMedium,
                    },
                    gestureEnabled: false,
                }}
            >
                <Stack.Screen
                    name="IJM"
                    component={Root}
                    options={{
                        headerShown: false,
                        animation: "slide_from_bottom",
                    }}
                />
                <Stack.Screen
                    name="GroupSelect"
                    component={GroupSelect}
                    options={{
                        headerShown: false,
                        animation: "slide_from_bottom",
                    }}
                />
                <Stack.Screen
                    name="WaitingSelect"
                    component={WaitingSelect}
                    options={{
                        headerShown: false,
                        animation: "slide_from_bottom",
                    }}
                />
                <Stack.Screen
                    name="CoverSelect"
                    component={CoverSelect}
                    options={{
                        headerShown: false,
                        animation: "slide_from_bottom",
                    }}
                />
            </Stack.Navigator>
        </StackWrapper>
    );
};

const useStyles = () => {
    const { roundness } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                stackWrapper: {
                    width: 700,
                    height: "85%",
                    maxHeight: 900,
                    margin: "auto",
                    borderRadius: roundness * 3,
                    overflow: "hidden",
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    boxShadow: "rgba(17, 12, 46, 0.15) 0px 48px 100px 0px",
                },
            }),
        [roundness],
    );
};
export default IJMEditorNavigator;
